import React, { useEffect, useMemo, useState, useCallback } from "react";
import { CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { DatePicker } from "./ui/date-picker";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Check, ArrowUpDown } from "lucide-react";
import fetchWithAuth from "../fetchWithAuth";

const API_URL = "/api/project";

// Утилиты
function stripProtocolAndWww(url) {
  return (url || "")
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "");
}
function formatYMD(d) {
  if (!d) return null;
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function toFixed1(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return "-";
  return (Math.round(n * 10) / 10).toFixed(1);
}
function formatDMY(ymd) {
  if (!ymd) return "-";
  const d = new Date(ymd + "T00:00:00");
  const pad = (n) => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}
function formatDateBadge(dateString) {
  if (!dateString) return "-";
  const d = new Date(dateString);
  const pad = (n) => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}
function displayCount(v) {
  return v == null ? "-" : String(v);
}
function buildPosMap(auditRows) {
  const map = new Map();
  for (const row of auditRows || []) {
    const norm = stripProtocolAndWww(row.url);
    if (!map.has(norm)) map.set(norm, []);
    map.get(norm).push(Number(row.position));
  }
  return map;
}
function calcMetricsFor(normUrl, posMap) {
  const list = posMap.get(normUrl) || [];
  if (list.length === 0) {
    return {
      rep10: null,
      rep20: null,
      avg: null,
    };
  }
  const rep10 = list.filter((p) => p <= 10).length;
  const rep20 = list.filter((p) => p <= 20).length;
  const avg =
    list.length > 0 ? list.reduce((a, b) => a + b, 0) / list.length : null;
  return { rep10, rep20, avg };
}

export default function CompareDynamicsTab({ projectId, promotedPages = [], blacklistPages = [] }) {
  const [dates, setDates] = useState([]);
  const [date1, setDate1] = useState(null); // YYYY-MM-DD
  const [date2, setDate2] = useState(null); // YYYY-MM-DD
  const [audit1, setAudit1] = useState([]);
  const [audit2, setAudit2] = useState([]);
  const [loading, setLoading] = useState(true);

  // Сортировка только по статусу для блока продвигаемых
  const [statusSort, setStatusSort] = useState("desc"); // desc: Active сверху

  const loadDates = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/${projectId}/arsenkin-dates`);
      const arr = await res.json();
      // arr отсортирован по дате desc на бэке
      setDates(arr);
      if (arr.length > 0) {
        const last = arr[0].slice(0, 10); // последняя (максимальная)
        const first = arr[arr.length - 1].slice(0, 10); // самая ранняя
        setDate1(first);
        setDate2(last);
      }
    } catch (e) {
      setDates([]);
    }
  }, [projectId]);

  const loadAudit = useCallback(async (d1, d2) => {
    if (!d1 && !d2) return;
    setLoading(true);
    try {
      let a1 = [];
      let a2 = [];
      if (d1) {
        const r1 = await fetchWithAuth(`${API_URL}/${projectId}/arsenkin?date=${d1}`);
        a1 = await r1.json();
      }
      if (d2) {
        const r2 = await fetchWithAuth(`${API_URL}/${projectId}/arsenkin?date=${d2}`);
        a2 = await r2.json();
      }
      setAudit1(a1 || []);
      setAudit2(a2 || []);
    } catch (e) {
      setAudit1([]);
      setAudit2([]);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    loadDates();
  }, [projectId, loadDates]);

  useEffect(() => {
    if (date1 || date2) {
      loadAudit(date1, date2);
    }
  }, [date1, date2, loadAudit]);

  // Карты позиций на каждую дату
  const map1 = useMemo(() => buildPosMap(audit1), [audit1]);
  const map2 = useMemo(() => buildPosMap(audit2), [audit2]);

  // Сортировка продвигаемых по статусу
  const sortedPromoted = useMemo(() => {
    const arr = [...promotedPages];
    arr.sort((a, b) => {
      const av = a.isActive ? 1 : 0;
      const bv = b.isActive ? 1 : 0;
      return statusSort === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [promotedPages, statusSort]);

  const markedList = useMemo(
    () => dates.map((d) => d.slice(0, 10)).filter((d) => d <= new Date().toISOString().slice(0, 10)),
    [dates]
  );

  // Определение цвета для второй ячейки
  // type: "promoted" | "blacklist"
  // metric: "rep10" | "rep20" | "avg"
  function secondCellColor(type, metric, v1, v2) {
    // Для повторов (rep10, rep20)
    if (metric !== "avg") {
      // Если в первой дате 0, во второй >0 — выделяем (продвигаемые: зелёный, вытесняемые: красный)
      if ((v1 === null || v1 === 0) && v2 > 0) {
        return type === "promoted" ? "bg-green-100" : "bg-red-100";
      }
      // Если в первой дате >0, во второй 0 — выделяем (продвигаемые: красный, вытесняемые: зелёный)
      if (v1 > 0 && (v2 === null || v2 === 0)) {
        return type === "promoted" ? "bg-red-100" : "bg-green-100";
      }
      // Рост — зелёный (продвигаемые), красный (вытесняемые)
      if (v2 > v1) {
        return type === "promoted" ? "bg-green-100" : "bg-red-100";
      }
      // Падение — красный (продвигаемые), зелёный (вытесняемые)
      if (v2 < v1) {
        return type === "promoted" ? "bg-red-100" : "bg-green-100";
      }
      // Без изменений — без цвета
      return "";
    }
    // Для средней позиции
    // Если в первой дате нет значения, а во второй есть — выделяем (продвигаемые: зелёный, вытесняемые: красный)
    if ((v1 === null || v1 === undefined) && v2 !== null && v2 !== undefined) {
      return type === "promoted" ? "bg-green-100" : "bg-red-100";
    }
    // Если оба значения есть
    if (v1 !== null && v2 !== null && v1 !== undefined && v2 !== undefined) {
      const improved = v2 < v1;
      if (type === "promoted") {
        return improved ? "bg-green-100" : v2 > v1 ? "bg-red-100" : "";
      } else {
        return improved ? "bg-red-100" : v2 > v1 ? "bg-green-100" : "";
      }
    }
    // В остальных случаях — без цвета
    return "";
  }

  function diffString(metric, v1, v2) {
    // Для повторов всегда приводим к числу
    if (metric !== "avg") {
      const n1 = v1 == null ? 0 : v1;
      const n2 = v2 == null ? 0 : v2;
      if (n1 === 0 && n2 === 0) return "";
      if (n1 === 0 && n2 > 0) return ` (+${n2})`;
      if (n1 > 0 && n2 === 0) return ` (-${n1})`;
      if (n1 !== n2) {
        const delta = n2 - n1;
        const sign = delta > 0 ? "+" : "";
        return ` (${sign}${delta})`;
      }
      return "";
    }
    // Для средней позиции (avg) — старая логика
    if ((v2 === null || v2 === undefined) && (v1 === null || v1 === undefined)) return "";
    if ((v1 === null || v1 === undefined) && v2 != null) {
      return "";
    }
    if ((v2 === null || v2 === undefined) && v1 != null) {
      return "";
    }
    if (v2 != null && v1 != null) {
      if (toFixed1(v2) === toFixed1(v1)) return "";
      const d = Math.round((v2 - v1) * 10) / 10;
      const sign = d > 0 ? "+" : "";
      return ` (${sign}${toFixed1(d)})`;
    }
    return "";
  }

  // Рендер бейджа статуса (как в продвигаемых страницах)
  function StatusBadge({ page }) {
    return page.isActive ? (
      <div className="flex flex-col items-center">
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1 w-[90px] justify-center">
          <Check className="h-3 w-3" />
          Active
        </Badge>
        {page.boostCount != null && (
          <div className="mt-1">
            <Badge
              variant="outline"
              className="bg-yellow-200 text-yellow-900 border-yellow-400 px-2 py-1 flex flex-row items-center gap-1"
              style={{ backgroundColor: "#FFD700", color: "#7c5a00", borderColor: "#FFD700" }}
            >
              {page.boostAt && (
                <div className="text-xs">
                  {formatDateBadge(page.boostAt)}
                </div>
              )}
              <div>+{page.boostCount}</div>
            </Badge>
          </div>
        )}
      </div>
    ) : (
      <div className="flex items-center justify-center">
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 w-[90px] justify-center flex items-center">
          Disabled
        </Badge>
      </div>
    );
  }

  // Рендер строки секции (общая логика)
  function renderRow(type, url, pageObj) {
    const norm = stripProtocolAndWww(url);
    const m1 = calcMetricsFor(norm, map1);
    const m2 = calcMetricsFor(norm, map2);

    // Для "прочерка" во 2 столбце: считаем, что "нет повторов" если rep20 === 0, а в первой было > 0
    // Тогда avg также считаем отсутствующим (null)
    // Для повторов всегда приводим к числу (0 если null/undefined)
    const v1 = {
      rep10: m1.rep10 == null ? 0 : m1.rep10,
      rep20: m1.rep20 == null ? 0 : m1.rep20,
      avg: m1.avg === null ? null : Number(m1.avg),
    };
    const v2raw = {
      rep10: m2.rep10 == null ? 0 : m2.rep10,
      rep20: m2.rep20 == null ? 0 : m2.rep20,
      avg: m2.avg === null ? null : Number(m2.avg),
    };

    // Принудительный прочерк для второй даты, если во вторую дату повторов нет (rep20=0), а в первую были (>0)
    // Но для diffString и отображения всегда используем 0
    const noRep2ButHad1 = v2raw.rep20 === 0 && v1.rep20 > 0;
    const v2 = {
      rep10: noRep2ButHad1 ? 0 : v2raw.rep10,
      rep20: noRep2ButHad1 ? 0 : v2raw.rep20,
      avg: noRep2ButHad1 ? null : v2raw.avg,
    };

    const t = type; // "promoted" | "blacklist"

    const rep10SecondBg = secondCellColor(t, "rep10", v1.rep10, v2.rep10);
    const rep20SecondBg = secondCellColor(t, "rep20", v1.rep20, v2.rep20);
    const avgSecondBg = secondCellColor(t, "avg", v1.avg, v2.avg);

    const firstPairSeparator = " border-r-4 border-gray-300";
    const secondPairSeparator = " border-r-4 border-gray-300";

    return (
      <TableRow key={url}>
        {type === "promoted" && (
          <TableCell className="w-[130px] text-center align-middle">
            <StatusBadge page={pageObj} />
          </TableCell>
        )}
        <TableCell style={{ width: 500, maxWidth: 500, minWidth: 500, padding: 0, verticalAlign: "middle" }}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline block"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              wordBreak: "break-all",
              width: "500px",
              maxWidth: "500px",
              minWidth: "500px",
              padding: "8px"
            }}
            title={url}
          >
            {url}
          </a>
        </TableCell>

        {/* Повторы топ-10 */}
        <TableCell className="w-[110px] text-center align-middle">
          {v1.rep10 == null ? 0 : v1.rep10}
        </TableCell>
        <TableCell className={`w-[150px] text-center align-middle${firstPairSeparator} ${rep10SecondBg}`}>
          {v2.rep10 == null ? "0" : `${displayCount(v2.rep10)}${diffString("rep10", v1.rep10, v2.rep10)}`}
        </TableCell>

        {/* Повторы топ-20 */}
        <TableCell className="w-[110px] text-center align-middle">
          {v1.rep20 == null ? 0 : v1.rep20}
        </TableCell>
        <TableCell className={`w-[150px] text-center align-middle${secondPairSeparator} ${rep20SecondBg}`}>
          {v2.rep20 == null ? "0" : `${displayCount(v2.rep20)}${diffString("rep20", v1.rep20, v2.rep20)}`}
        </TableCell>

        {/* Средняя позиция */}
        <TableCell className="w-[130px] text-center align-middle">
          {v1.avg === null ? "-" : toFixed1(v1.avg)}
        </TableCell>
        <TableCell className={`w-[170px] text-center align-middle ${avgSecondBg}`}>
          {v2.avg === null
            ? "-"
            : `${toFixed1(v2.avg)}${diffString("avg", v1.avg, v2.avg)}`}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <div className="space-y-6">
      {/* Выбор дат */}
      <div className="flex flex-col gap-3">
        <div className="font-bold text-s text-gray-800">
          Выбор дат
        </div>
        <div className="flex flex-row items-center gap-4">
          <div>
            <div className="text-xs text-gray-600 mb-1">Дата 1</div>
            <div style={{ width: 160 }}>
              <DatePicker
                value={date1 ? new Date(date1 + "T00:00:00") : null}
                onChange={(d) => {
                  if (d instanceof Date && !isNaN(d)) setDate1(formatYMD(d));
                }}
                markedDates={markedList}
              />
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-600 mb-1">Дата 2</div>
            <div style={{ width: 160 }}>
              <DatePicker
                value={date2 ? new Date(date2 + "T00:00:00") : null}
                onChange={(d) => {
                  if (d instanceof Date && !isNaN(d)) setDate2(formatYMD(d));
                }}
                markedDates={markedList}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Продвигаемые страницы */}
      <div>
        <CardHeader className="px-0 pt-0">
          <CardTitle>Продвигаемые страницы</CardTitle>
        </CardHeader>

        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[130px] text-center align-middle">
                  <Button
                    variant="ghost"
                    onClick={() => setStatusSort((s) => (s === "asc" ? "desc" : "asc"))}
                    className="flex items-center gap-1 font-medium justify-center"
                  >
                    Статус
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>URL</TableHead>
                <TableHead className="text-center border-r-4 border-gray-300" colSpan={2}>
                  Повторы топ-10
                </TableHead>
                <TableHead className="text-center border-r-4 border-gray-300" colSpan={2}>
                  Повторы топ-20
                </TableHead>
                <TableHead className="text-center" colSpan={2}>
                  Средняя позиция
                </TableHead>
              </TableRow>
              <TableRow>
                <TableHead></TableHead>
                <TableHead></TableHead>
                <TableHead className="text-center">{formatDMY(date1)}</TableHead>
                <TableHead className="text-center border-r-4 border-gray-300">{formatDMY(date2)}</TableHead>
                <TableHead className="text-center">{formatDMY(date1)}</TableHead>
                <TableHead className="text-center border-r-4 border-gray-300">{formatDMY(date2)}</TableHead>
                <TableHead className="text-center">{formatDMY(date1)}</TableHead>
                <TableHead className="text-center">{formatDMY(date2)}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : sortedPromoted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4 text-gray-500">
                    Нет продвигаемых страниц
                  </TableCell>
                </TableRow>
              ) : (
                sortedPromoted.map((p) => renderRow("promoted", p.url, p))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Вытесняемые страницы */}
      <div>
        <CardHeader className="px-0 pt-0">
          <CardTitle>Вытесняемые страницы</CardTitle>
        </CardHeader>

        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>URL</TableHead>
                <TableHead className="text-center border-r-4 border-gray-300" colSpan={2}>
                  Повторы топ-10
                </TableHead>
                <TableHead className="text-center border-r-4 border-gray-300" colSpan={2}>
                  Повторы топ-20
                </TableHead>
                <TableHead className="text-center" colSpan={2}>
                  Средняя позиция
                </TableHead>
              </TableRow>
              <TableRow>
                <TableHead></TableHead>
                <TableHead className="text-center">{formatDMY(date1)}</TableHead>
                <TableHead className="text-center border-r-4 border-gray-300">{formatDMY(date2)}</TableHead>
                <TableHead className="text-center">{formatDMY(date1)}</TableHead>
                <TableHead className="text-center border-r-4 border-gray-300">{formatDMY(date2)}</TableHead>
                <TableHead className="text-center">{formatDMY(date1)}</TableHead>
                <TableHead className="text-center">{formatDMY(date2)}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : blacklistPages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-4 text-gray-500">
                    Нет вытесняемых страниц
                  </TableCell>
                </TableRow>
              ) : (
                blacklistPages.map((bp) => renderRow("blacklist", bp.blackUrl, bp))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
